import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { LabelL } from '@sorare/core/src/atoms/typography';
import { Dialog } from '@sorare/core/src/components/dialog';
import { glossary } from '@sorare/core/src/lib/glossary';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { PackOdds } from 'components/commonCard/PackContentDialog/PackOdds';

import { StarPackContents } from './StarPackContents';
import type { StarPackContentDialog_cardPack } from './__generated__/index.graphql';

const StyledDialog = styled(Dialog)`
  @media ${tabletAndAbove} {
    width: 500px;
    min-height: 500px;
  }
`;

const ContentWrapper = styled.div`
  padding: var(--double-unit);
`;

const StyledTab = styled(Tab)`
  & > * {
    text-transform: capitalize;
  }
`;

type Props = {
  open: boolean;
  onClose: () => void;
  cardPack: StarPackContentDialog_cardPack;
};

export const StarPackContentDialog = ({ open, onClose, cardPack }: Props) => {
  const [tab, setTab] = useState<'contents' | 'odds'>('contents');
  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      title={
        <LabelL bold className="text-center">
          <FormattedMessage
            defaultMessage="Pack Contents"
            id="StarPackContentDialog.title"
          />
        </LabelL>
      }
    >
      <TabBar
        fullWidth
        variant="flat"
        forceMobileLayout
        onValueChange={value => setTab(value as 'contents' | 'odds')}
        value={tab}
      >
        <StyledTab value="contents">
          <FormattedMessage
            id="StarPackContentDialog.contents"
            defaultMessage="Contents"
          />
        </StyledTab>
        <StyledTab value="odds">
          <FormattedMessage {...glossary.odds} />
        </StyledTab>
      </TabBar>
      <ContentWrapper>
        {tab === 'contents' && <StarPackContents cardPack={cardPack} />}
        {tab === 'odds' && <PackOdds cardPack={cardPack} />}
      </ContentWrapper>
    </StyledDialog>
  );
};

StarPackContentDialog.fragments = {
  cardPack: gql`
    fragment StarPackContentDialog_cardPack on CardPack {
      slug
      ...PackOdds_cardPack
      ...StarPackContents_cardPack
    }
    ${PackOdds.fragments.cardPack}
    ${StarPackContents.fragments.cardPack}
  ` as TypedDocumentNode<StarPackContentDialog_cardPack>,
};
